import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraEmptyState, SpecraTabBar } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTablePreferences } from '../../../../hooks/useTablePreferences';
import { EntityTable } from '../../../Delivery/_shared/components/EntityTable';
import { AiAssistButton } from '../../AiAssist';
import { INTERACTION_COLUMNS } from '../../CaptureDetailPage';
import { CaptureStakeholdersSection } from '../../CaptureStakeholdersSection';
import { useCaptureWorkspace } from '../../hooks';
import { IncomingCustomerContext } from '../../IncomingCustomerContext';
import { TouchpointEventCell } from '../../TouchpointEventCell';
import { TouchpointSheet } from '../../TouchpointSheet';
import { PursuitHearsayBlock } from '../hearsay/PursuitHearsayBlock';
import { usePursuitRecord } from '../PursuitRecordContext';
import { PursuitSectionFrame } from '../PursuitSectionFrame';
/** The Customer & touchpoints page tabs; Customer themes is its own route (`customer/themes`). */ export function CustomerTabs({ value, onValueChange }) {
    const { captureProjectId } = usePursuitRecord();
    const navigate = useNavigate();
    return /*#__PURE__*/ _jsx(SpecraTabBar, {
        idBase: "pursuit-customer",
        label: "Customer",
        onValueChange: (tab)=>{
            if (tab === 'themes') navigate(`/capture/${captureProjectId}/customer/themes`);
            else if (onValueChange) onValueChange(tab);
            else navigate(`/capture/${captureProjectId}/customer${tab === 'people' ? '?view=people' : ''}`);
        },
        options: [
            {
                value: 'touchpoints',
                label: 'Touchpoints',
                testId: Testids.pursuitCustomer.tabTouchpoints
            },
            {
                value: 'people',
                label: 'People',
                testId: Testids.pursuitCustomer.tabPeople
            },
            {
                value: 'themes',
                label: 'Customer themes',
                testId: Testids.capture.tabThemes
            }
        ],
        value: value
    });
}
const CUSTOMER_COLUMNS = [
    ...INTERACTION_COLUMNS,
    {
        key: 'eventId',
        header: 'Event',
        render: (row)=>/*#__PURE__*/ _jsx(TouchpointEventCell, {
                eventId: row.eventId,
                title: row.eventTitle
            })
    }
];
/**
 * Customer & touchpoints (legacy `?tab=interactions`): incoming customer
 * context, then Touchpoints (the interaction log and its sheet) and People
 * (the pursuit's stakeholders).
 */ export function CustomerSection() {
    const { captureProjectId, zouProjectId, capabilities, correlationId, header } = usePursuitRecord();
    // GRW-30: sections always sit in the record shell, so the gate is the header capabilities.
    const { canEdit, canWriteEvents } = usePursuitRecord().capabilities;
    const { data } = useCaptureWorkspace(captureProjectId);
    const prefs = useTablePreferences('ts:capture:interactions');
    const [params] = useSearchParams();
    const [tab, setTab] = useState(()=>params.get('view') === 'people' ? 'people' : 'touchpoints');
    const [sheet, setSheet] = useState({
        open: false,
        value: null
    });
    const name = header?.name ?? data?.summary?.name ?? '';
    return /*#__PURE__*/ _jsxs(PursuitSectionFrame, {
        title: "Customer & touchpoints",
        children: [
            /*#__PURE__*/ _jsx(IncomingCustomerContext, {
                customerOrgId: data?.summary?.customerOrgId
            }),
            /*#__PURE__*/ _jsx(PursuitHearsayBlock, {
                captureProjectId: captureProjectId,
                meetings: (data?.interactions ?? []).map((i)=>({
                        id: i.id,
                        title: i.title ?? '',
                        channel: i.channel,
                        interactionDate: i.interactionDate?.toDate()
                    })),
                pursuitName: name
            }),
            /*#__PURE__*/ _jsx(CustomerTabs, {
                onValueChange: setTab,
                value: tab
            }),
            tab === 'touchpoints' ? /*#__PURE__*/ _jsxs("div", {
                className: "space-y-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(AiAssistButton, {
                                action: "summarize-interactions",
                                label: "Summarize",
                                prompt: "Summarize the customer interactions logged on this capture. Call out positive signals, risks, and the follow-ups we owe.",
                                zouProjectId: zouProjectId
                            }),
                            capabilities.canEdit && (data?.interactions ?? []).length > 0 && /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.capture.addInteractionButton,
                                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                    className: "h-4 w-4"
                                }),
                                onClick: ()=>setSheet({
                                        open: true,
                                        value: null
                                    }),
                                size: "sm",
                                children: "Log Touchpoint"
                            })
                        ]
                    }),
                    (data?.interactions ?? []).length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        size: "compact",
                        title: "No touchpoints yet",
                        description: "Log meetings, calls, emails and conferences with the customer.",
                        "data-testid": Testids.pursuitCustomer.emptyState,
                        action: capabilities.canEdit ? /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            "data-testid": Testids.capture.addInteractionButton,
                            onClick: ()=>setSheet({
                                    open: true,
                                    value: null
                                }),
                            children: "Log Touchpoint"
                        }) : undefined
                    }) : /*#__PURE__*/ _jsx(EntityTable, {
                        columns: CUSTOMER_COLUMNS,
                        data: data?.interactions ?? [],
                        filters: prefs.filters,
                        onFilterChange: prefs.setFilter,
                        onRowClick: (r)=>setSheet({
                                open: true,
                                value: r
                            }),
                        onToggleSort: prefs.toggleSort,
                        rowKey: (r)=>r.id,
                        rowTestid: Testids.capture.interactionRow,
                        sort: prefs.sort,
                        testid: Testids.capture.interactionTable
                    })
                ]
            }) : /*#__PURE__*/ _jsx(CaptureStakeholdersSection, {
                readOnly: !canEdit,
                captureProjectId: captureProjectId,
                correlationId: correlationId
            }),
            sheet.open && /*#__PURE__*/ _jsx(TouchpointSheet, {
                captureProjectId: captureProjectId,
                correlationId: correlationId,
                interaction: sheet.value,
                onClose: ()=>setSheet((s)=>({
                            ...s,
                            open: false
                        })),
                pursuitName: name,
                zouProjectId: zouProjectId,
                events: {
                    canWrite: canWriteEvents,
                    opportunityId: header?.opportunity?.opportunityId
                }
            })
        ]
    });
}
